import { jsPDF } from 'jspdf'
import moment from 'moment'
import type { DrinkEntry, SessionStats } from '@/types'
import { METABOLISM_RATE } from '@/utils/bac'
import { standardDrinks, sessionStandardDrinks, computeRiskAssessment } from '@/utils/health'

interface ReportData {
  entries: DrinkEntry[]
  weight: number
  gender: 'male' | 'female'
  allSessions: SessionStats[]
}

export function generateReport({ entries, weight, gender, allSessions }: ReportData): void {
  const nonGapEntries = entries.filter(e => !e.is_gap)

  if (nonGapEntries.length === 0) {
    alert('No drink entries to generate a report from.')
    return
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 20
  const contentWidth = pageWidth - margin * 2
  let y = margin

  function checkPageBreak(needed: number) {
    if (y + needed > 275) {
      doc.addPage()
      y = margin
    }
  }

  function heading(text: string) {
    checkPageBreak(14)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text(text, margin, y)
    y += 4
    doc.setDrawColor(180)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6
  }

  function label(key: string, value: string) {
    checkPageBreak(7)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text(key + ':', margin, y)
    doc.setFont('helvetica', 'normal')
    doc.text(value, margin + 55, y)
    y += 6
  }

  function para(text: string) {
    checkPageBreak(12)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    const lines = doc.splitTextToSize(text, contentWidth) as string[]
    for (const line of lines) {
      checkPageBreak(5)
      doc.text(line, margin, y)
      y += 5
    }
    y += 2
  }

  // ── Header ──
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.text('Alcohol Consumption Report', margin, y)
  y += 8
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text('Generated: ' + moment().format('dddd D MMMM YYYY, HH:mm'), margin, y)
  y += 10

  // ── Patient Summary ──
  heading('Patient Summary')
  label('Weight', `${weight} kg`)
  label('Gender', gender.charAt(0).toUpperCase() + gender.slice(1))
  y += 4

  // ── Reporting Period ──
  const firstEntry = nonGapEntries[0]
  const lastEntry = nonGapEntries[nonGapEntries.length - 1]
  const firstDate = moment(firstEntry.finished_at)
  const lastDate = moment(lastEntry.finished_at)
  const totalDays = Math.max(1, lastDate.diff(firstDate, 'days') + 1)

  heading('Reporting Period')
  label('From', firstDate.format('D MMMM YYYY'))
  label('To', lastDate.format('D MMMM YYYY'))
  label('Duration', `${totalDays} days`)
  if (totalDays < 7) {
    y += 2
    doc.setFontSize(9)
    doc.setFont('helvetica', 'italic')
    doc.text('Note: Reporting period is less than 7 days. Weekly averages may not be representative.', margin, y)
    doc.setFont('helvetica', 'normal')
    y += 6
  }
  y += 4

  // ── Consumption Summary ──
  const totalStdDrinks = nonGapEntries.reduce((sum, e) => sum + standardDrinks(e), 0)
  const avgDrinksPerSession = allSessions.length > 0
    ? nonGapEntries.length / allSessions.length
    : 0
  const avgStdDrinksPerSession = allSessions.length > 0
    ? totalStdDrinks / allSessions.length
    : 0
  const peakBAC = Math.max(...nonGapEntries.map(e => e.bac || 0))
  const avgSessionPeakBAC = allSessions.length > 0
    ? allSessions.reduce((sum, s) => sum + s.peakBac, 0) / allSessions.length
    : 0

  heading('Consumption Summary')
  label('Total drinks logged', String(nonGapEntries.length))
  label('Total sessions', String(allSessions.length))
  label('Avg drinks per session', avgDrinksPerSession.toFixed(1))
  label('Avg standard drinks/session', avgStdDrinksPerSession.toFixed(1))
  label('Total standard drinks', totalStdDrinks.toFixed(1))
  label('Peak BAC (all time)', peakBAC.toFixed(3) + '%')
  label('Avg session peak BAC', avgSessionPeakBAC.toFixed(3) + '%')
  y += 4

  // ── Risk Assessment (NHMRC Guidelines) ──
  const risk = computeRiskAssessment(entries, allSessions)
  const totalWeeks = Math.max(1, totalDays / 7)
  const weeklyAvgStdDrinks = risk?.weeklyAvgStdDrinks ?? (totalStdDrinks / totalWeeks)
  const maxSessionStdDrinks = risk?.maxSessionStdDrinks ?? (
    allSessions.length > 0
      ? Math.max(...allSessions.map(s => sessionStandardDrinks(s)))
      : 0
  )

  let riskLevel: string
  let riskExplanation: string

  if (!risk || risk.level === 'LOW') {
    riskLevel = 'LOW RISK'
    riskExplanation =
      'Your consumption is within the NHMRC guideline limits of no more than 10 standard drinks per week ' +
      'and no more than 4 standard drinks on any single occasion. ' +
      'While no level of alcohol consumption is completely risk-free, your current pattern represents a lower risk of harm.'
  } else if (risk.level === 'INCREASED') {
    riskLevel = 'INCREASED RISK'
    riskExplanation =
      'Your consumption exceeds NHMRC guidelines in at least one measure. ' +
      'The NHMRC recommends no more than 10 standard drinks per week and no more than 4 standard drinks on any single occasion. ' +
      'Reducing your intake would lower your risk of alcohol-related harm. ' +
      'Consider discussing strategies with your GP.'
  } else {
    riskLevel = 'HIGH RISK'
    riskExplanation =
      'Your consumption significantly exceeds NHMRC guidelines. ' +
      'The NHMRC recommends no more than 10 standard drinks per week and no more than 4 standard drinks on any single occasion. ' +
      'At this level, you are at substantially increased risk of alcohol-related disease, injury, and other harms. ' +
      'It is strongly recommended that you discuss your drinking with a healthcare professional.'
  }

  heading('Risk Assessment (NHMRC Guidelines)')
  label('Weekly avg standard drinks', weeklyAvgStdDrinks.toFixed(1))
  label('Max session standard drinks', maxSessionStdDrinks.toFixed(1))
  label('Risk classification', riskLevel)
  y += 2
  para(riskExplanation)
  y += 4

  // ── Last 30 Days ──
  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day')
  const recentEntries = nonGapEntries.filter(e => moment(e.finished_at).isAfter(thirtyDaysAgo))

  heading('Last 30 Days')

  if (recentEntries.length === 0) {
    para('No drinks recorded in the last 30 days.')
  } else {
    // Build 4 weekly buckets
    for (let w = 0; w < 4; w++) {
      const weekStart = moment().subtract((4 - w) * 7, 'days').startOf('day')
      const weekEnd = moment().subtract((3 - w) * 7, 'days').startOf('day')
      const weekEntries = recentEntries.filter(e => {
        const m = moment(e.finished_at)
        return m.isSameOrAfter(weekStart) && m.isBefore(weekEnd)
      })
      const weekStd = weekEntries.reduce((sum, e) => sum + standardDrinks(e), 0)
      label(
        `Week ${w + 1} (${weekStart.format('D MMM')} – ${weekEnd.clone().subtract(1, 'day').format('D MMM')})`,
        `${weekEntries.length} drinks, ${weekStd.toFixed(1)} std drinks`
      )
    }
  }
  y += 4

  // ── Periods of Abstinence ──
  heading('Periods of Abstinence')

  // Logged tracking gaps (unknown periods) — exclude any streak that overlaps one.
  const gapPeriods = entries.filter(e => e.is_gap).map(e => ({
    start: new Date(e.started_at || e.finished_at).getTime(),
    end: new Date(e.finished_at).getTime(),
  }))
  const overlapsGap = (start: number, end: number): boolean =>
    gapPeriods.some(g => g.start < end && g.end > start)

  // Current abstinence: time since last logged drink, if not overlapping a gap.
  const lastDrinkTime = moment(lastEntry.finished_at)
  const nowMoment = moment()
  const lastBac = lastEntry.bac || 0
  const stillAffected = nowMoment.diff(lastDrinkTime, 'hours', true) < (lastBac / METABOLISM_RATE)
  const currentlyAbstinent =
    !stillAffected && !overlapsGap(lastDrinkTime.valueOf(), nowMoment.valueOf())
  const currentAbstinenceSeconds = currentlyAbstinent
    ? nowMoment.diff(lastDrinkTime, 'seconds')
    : 0

  if (currentlyAbstinent) {
    label('Currently abstinent', `${formatDuration(currentAbstinenceSeconds)}`)
    label('  Since last drink on', lastDrinkTime.format('D MMMM YYYY'))
  } else {
    label('Currently abstinent', 'No')
    label('  Last drink', lastDrinkTime.format('D MMMM YYYY'))
  }

  // Calendar dry days within the reporting period (date-based, not duration).
  const drinkDates = new Set(nonGapEntries.map(e => moment(e.finished_at).format('YYYY-MM-DD')))
  const dryDays = Math.max(0, totalDays - drinkDates.size)
  const dryPct = totalDays > 0 ? Math.round((dryDays / totalDays) * 100) : 0
  label('Dry days in period', `${dryDays} of ${totalDays} (${dryPct}%)`)

  // Build all abstinence stretches: gaps between consecutive sessions +
  // the live (still ongoing) stretch if currently abstinent.
  interface AbstinencePeriod {
    startedAt: number
    endedAt: number
    durationSeconds: number
    isCurrent: boolean
  }
  const periods: AbstinencePeriod[] = []
  for (let i = 0; i < allSessions.length - 1; i++) {
    const session = allSessions[i]
    const nextSession = allSessions[i + 1]
    const sessionLastDrink = session.drinks[session.drinks.length - 1]
    const start = moment(sessionLastDrink.finished_at).valueOf()
    const end = moment(nextSession.drinks[0].finished_at).valueOf()
    const durationSeconds = Math.floor((end - start) / 1000)
    if (durationSeconds > 0 && !overlapsGap(start, end)) {
      periods.push({ startedAt: start, endedAt: end, durationSeconds, isCurrent: false })
    }
  }
  if (currentlyAbstinent && currentAbstinenceSeconds > 0) {
    periods.push({
      startedAt: lastDrinkTime.valueOf(),
      endedAt: nowMoment.valueOf(),
      durationSeconds: currentAbstinenceSeconds,
      isCurrent: true,
    })
  }
  periods.sort((a, b) => b.durationSeconds - a.durationSeconds)

  if (periods.length > 0) {
    const avgGap = periods.reduce((sum, p) => sum + p.durationSeconds, 0) / periods.length
    const longestDurationSeconds = periods[0].durationSeconds  // periods are sorted desc
    label('Longest abstinence', formatDuration(longestDurationSeconds))
    label('Total abstinence stretches', String(periods.length))
    label('Avg gap between sessions', formatDuration(avgGap))
    y += 2

    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    checkPageBreak(7)
    doc.text('All abstinence stretches (most recent first):', margin, y)
    y += 6
    doc.setFont('helvetica', 'normal')

    // Chronological (newest first) so the doctor reads progress top-down.
    const chronological = [...periods].sort((a, b) => b.startedAt - a.startedAt)
    for (let i = 0; i < chronological.length; i++) {
      const p = chronological[i]
      const range = `${moment(p.startedAt).format('D MMM YYYY')} – ${
        p.isCurrent ? 'ongoing' : moment(p.endedAt).format('D MMM YYYY')
      }`
      const tags: string[] = []
      if (p.isCurrent) tags.push('current')
      // Tag the longest, but don't double-tag if current also happens to be longest.
      if (!p.isCurrent && p.durationSeconds === longestDurationSeconds) tags.push('longest')
      const suffix = tags.length > 0 ? ` (${tags.join(', ')})` : ''
      label(`  ${i + 1}.`, `${formatDuration(p.durationSeconds)}${suffix}  ·  ${range}`)
    }
  } else if (currentlyAbstinent) {
    label('Avg gap between sessions', 'N/A')
    para('Only one drinking episode logged — abstinence trend will appear after more data is collected.')
  } else {
    para('Not enough data to identify periods of abstinence.')
  }
  y += 4

  // ── Worst Cases ──
  heading('Worst Cases')

  if (allSessions.length === 0) {
    para('No sessions recorded.')
  } else {
    const worstBacSession = allSessions.reduce((worst, s) =>
      s.peakBac > worst.peakBac ? s : worst
    )
    const mostDrinksSession = allSessions.reduce((worst, s) =>
      s.drinkCount > worst.drinkCount ? s : worst
    )
    const worstBacStdDrinks = sessionStandardDrinks(worstBacSession)

    label('Highest peak BAC', `${worstBacSession.peakBac.toFixed(3)}%`)
    label('  Drinks in that session', `${worstBacSession.drinkCount} (${worstBacStdDrinks.toFixed(1)} std drinks)`)
    label('  Date', moment(worstBacSession.drinks[0]?.finished_at).format('D MMMM YYYY'))

    if (mostDrinksSession !== worstBacSession) {
      y += 2
      const mostDrinksStdDrinks = sessionStandardDrinks(mostDrinksSession)
      label('Most drinks in a session', `${mostDrinksSession.drinkCount} (${mostDrinksStdDrinks.toFixed(1)} std drinks)`)
      label('  Peak BAC that session', `${mostDrinksSession.peakBac.toFixed(3)}%`)
      label('  Date', moment(mostDrinksSession.drinks[0]?.finished_at).format('D MMMM YYYY'))
    }
  }
  y += 4

  // ── Footer / Disclaimer ──
  checkPageBreak(20)
  y += 4
  doc.setDrawColor(180)
  doc.line(margin, y, pageWidth - margin, y)
  y += 6
  doc.setFontSize(8)
  doc.setFont('helvetica', 'italic')
  const disclaimer =
    'This report is based on self-reported data and should be interpreted in consultation with a qualified health professional. ' +
    'BAC calculations are estimates using the Widmark formula and may not reflect actual blood alcohol levels. ' +
    'Standard drink calculations use the Australian standard of 10g pure alcohol. ' +
    'Generated by SSDL.app.'
  const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth) as string[]
  for (const line of disclaimerLines) {
    checkPageBreak(4)
    doc.text(line, margin, y)
    y += 4
  }

  // Save
  const filename = `alcohol-report-${moment().format('YYYY-MM-DD')}.pdf`
  doc.save(filename)
}

function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  parts.push(`${minutes}m`)
  return parts.join(' ')
}
